import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  Tag,
  message,
  Popconfirm,
  Statistic,
  Spin,
  Empty,
  Upload,
  Alert,
  Grid
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  EyeOutlined,
  WalletOutlined,
  UploadOutlined,
  SearchOutlined,
  FilterOutlined,
  InboxOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import api from '../utils/api';
import ColumnsCardList from '../components/common/ColumnsCardList';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/fr_FR';
import { getDeviseActive } from '../utils/devise';

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

/**
 * Composant principal pour la gestion des dépenses
 * Permet de créer, modifier, supprimer et visualiser les dépenses
 * Affiche également des statistiques et graphiques de répartition
 */
const Expenses = () => {
  // Mobile (< md) : cartes à la place du tableau, graphique allégé ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  // États pour les données
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [analyzingFile, setAnalyzingFile] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);  // Fichier en attente d'upload (stocké jusqu'à la validation)
  // File locale des factures sélectionnées : une seule est analysée et enregistrée à la fois.
  const [invoiceQueue, setInvoiceQueue] = useState([]);
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(-1);

  // États pour les options d'analyse IA de la facture
  const [analyzeLanguage, setAnalyzeLanguage] = useState('fr');    // Langue demandée à l'IA
  const [analyzeCurrency, setAnalyzeCurrency] = useState(getDeviseActive());   // Devise de l'utilisateur
  const [exchangeRate, setExchangeRate] = useState(1);             // Taux de conversion
  const [invoiceCurrency, setInvoiceCurrency] = useState(null);    // Devise détectée dans la facture
  const [rawAnalysisData, setRawAnalysisData] = useState(null);    // Données brutes IA (montant original avant conversion)
  const [duplicateWarning, setDuplicateWarning] = useState(null);  // Info doublon détecté
  
  // États pour les modales
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  // Dépense dont le justificatif est ouvert dans la modale de prévisualisation.
  const [previewingReceipt, setPreviewingReceipt] = useState(null);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    category: null,
    status: null,
    dateRange: [dayjs().subtract(30, 'day'), dayjs()],
    search: ''
  });
  
  // États pour la pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [form] = Form.useForm();

  // Catégories de dépenses avec traduction en français
  const categories = [
    { value: 'loyer', label: 'Loyer', color: 'blue' },
    { value: 'salaires', label: 'Salaires', color: 'green' },
    { value: 'stock', label: 'Stock / Approvisionnement', color: 'orange' },
    { value: 'publicité', label: 'Publicité / Marketing', color: 'purple' },
    { value: 'transport', label: 'Transport / Livraison', color: 'cyan' },
    { value: 'utilities', label: 'Électricité / Eau / Internet', color: 'red' },
    { value: 'autres', label: 'Autres', color: 'gray' }
  ];

  // Statuts de dépenses
  const statuses = [
    { value: 'paid', label: 'Payée', color: 'success' },
    { value: 'pending', label: 'En attente', color: 'warning' },
    { value: 'cancelled', label: 'Annulée', color: 'default' }
  ];

  // Couleurs pour le graphique en camembert
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

  /**
   * Récupérer toutes les dépenses avec les filtres appliqués
   * Gère également la pagination
   */
  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize,
        orderBy: 'expense_date',
        ascending: false
      };

      // Appliquer les filtres
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].startOf('day').toISOString();
        params.end_date = filters.dateRange[1].endOf('day').toISOString();
      }

      const response = await api.get('/expenses', { params });
      
      setExpenses(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));

    } catch (error) {
      console.error('Erreur lors de la récupération des dépenses:', error);
      message.error('Erreur lors de la récupération des dépenses');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current, pagination.pageSize]);

  /**
   * Récupérer les statistiques globales des dépenses
   * Total, moyenne, nombre de dépenses
   */
  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].startOf('day').toISOString();
        params.end_date = filters.dateRange[1].endOf('day').toISOString();
      }

      const response = await api.get('/expenses/stats', { params });
      setStats(response.data);

    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
    }
  }, [filters.dateRange]);

  /**
   * Récupérer la répartition des dépenses par catégorie
   * Pour affichage dans le graphique en camembert
   */
  const fetchCategoryData = useCallback(async () => {
    try {
      const params = {};
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].startOf('day').toISOString();
        params.end_date = filters.dateRange[1].endOf('day').toISOString();
      }

      const response = await api.get('/expenses/by-category', { params });
      
      // Formater les données pour le graphique
      const formattedData = response.data.map(item => {
        const cat = categories.find(c => c.value === item.category);
        return {
          name: cat ? cat.label : item.category,
          value: item.totalAmount,
          count: item.expenseCount
        };
      });
      
      setCategoryData(formattedData);

    } catch (error) {
      console.error('Erreur lors de la récupération des données par catégorie:', error);
    }
  }, [filters.dateRange]);

  /**
   * Effet pour charger les données initiales et à chaque changement de filtres
   */
  useEffect(() => {
    fetchExpenses();
    fetchStats();
    fetchCategoryData();
  }, [fetchExpenses, fetchStats, fetchCategoryData]);

  /**
   * useEffect : applique la conversion de devise LOCALEMENT chaque fois que le taux change.
   * Utilise les données brutes stockées (rawAnalysisData) pour recalculer le montant
   * sans refaire un appel à l'IA.
   */
  useEffect(() => {
    if (rawAnalysisData && rawAnalysisData.amount !== null && rawAnalysisData.amount !== undefined) {
      const converted = Math.round(rawAnalysisData.amount * exchangeRate);
      form.setFieldsValue({ amount: converted });
    }
  }, [exchangeRate, rawAnalysisData, form]);

  /**
   * Analyse une facture unique avec l'IA et prépare sa création sans l'enregistrer immédiatement.
   * Cette fonction est volontairement appelée une seule fois par document afin de préserver l'ordre
   * de la file et de ne pas surcharger le fournisseur d'IA avec plusieurs analyses simultanées.
   * @param {File} file - Le fichier image ou PDF correspondant à la facture à analyser.
   */
  const analyzeReceiptFile = async (file) => {
    try {
      setAnalyzingFile(true);
      setInvoiceCurrency(null);
      setRawAnalysisData(null);
      setDuplicateWarning(null);
      setPendingFile(file);  // Stocker le fichier : l'upload réel se fera à la validation

      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', analyzeLanguage);
      formData.append('userCurrency', analyzeCurrency);
      // On n'envoie plus le taux : la conversion est faite côté frontend
      
      message.loading({ content: "Analyse de la facture par l'IA en cours...", key: 'analyze' });
      
      const response = await api.post('/expenses/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { data, duplicate, existingExpense } = response.data;

      // Détection doublon
      if (duplicate && existingExpense) {
        setDuplicateWarning(existingExpense);
        message.warning({ content: `Facture déjà enregistrée (N° ${data.invoice_number}). Vérifiez avant de continuer.`, key: 'analyze', duration: 6 });
      }
      
      if (data) {
        // Stocker les données brutes (montant original) pour la conversion locale ultérieure
        setRawAnalysisData(data);

        // Appliquer le taux courant immédiatement
        const convertedAmount = data.amount ? Math.round(data.amount * exchangeRate) : data.amount;

        const updates = {};
        if (convertedAmount !== null && convertedAmount !== undefined) updates.amount = convertedAmount;
        if (data.category) updates.category = data.category;
        if (data.description) updates.description = data.description;
        if (data.expense_date) updates.expense_date = dayjs(data.expense_date);
        if (data.invoice_number) updates.invoice_number = data.invoice_number;
        // receipt_url non pré-rempli ici : l'upload se fait uniquement à la validation du formulaire
        
        form.setFieldsValue(updates);

        // Si l'IA a détecté une devise différente, afficher le champ taux
        if (data.invoice_currency && data.invoice_currency !== analyzeCurrency) {
          setInvoiceCurrency(data.invoice_currency);
          if (!duplicate) {
            message.warning({ 
              content: `Devise détectée : ${data.invoice_currency}. Ajustez le taux de conversion si nécessaire.`, 
              key: 'analyze', 
              duration: 5 
            });
          }
        } else if (!duplicate) {
          message.success({ content: 'Facture analysée avec succès !', key: 'analyze', duration: 3 });
        }
      }
      
    } catch (err) {
      console.error(err);
      message.error({ 
        content: err.response?.data?.error || "Erreur lors de l'analyse IA. Veuillez remplir manuellement.", 
        key: 'analyze', 
        duration: 3 
      });
    } finally {
      setAnalyzingFile(false);
    }
  };

  /**
   * Crée une file de factures depuis la sélection multiple, puis déclenche seulement la première analyse.
   * Ant Design appelle cette fonction pour chaque fichier choisi : seul le premier appel traite la liste
   * complète afin d'éviter d'ajouter plusieurs fois les mêmes factures dans la file locale.
   * @param {File} file - Le fichier traité par l'appel courant d'Ant Design.
   * @param {File[]} fileList - La liste complète des fichiers sélectionnés par l'utilisateur.
   * @returns {boolean} False pour empêcher Ant Design d'effectuer son upload automatique.
   */
  const handleReceiptQueue = (file, fileList) => {
    if (file.uid !== fileList[0]?.uid) return Upload.LIST_IGNORE;

    const filesToProcess = editingExpense ? [fileList[0]] : fileList;
    setInvoiceQueue(filesToProcess);
    setCurrentInvoiceIndex(0);
    analyzeReceiptFile(filesToProcess[0]);

    return Upload.LIST_IGNORE;
  };

  /**
   * Réinitialise les données liées à une analyse de facture afin que le formulaire suivant parte vierge.
   * Les valeurs par défaut de date et de statut restent cohérentes avec une nouvelle dépense manuelle.
   * Le taux peut être conservé pendant une file pour éviter de le ressaisir à chaque facture dans la même devise.
   * @param {Object} options - Options de réinitialisation de l'analyse en cours.
   * @param {boolean} options.preserveExchangeRate - Conserve le taux de conversion déjà saisi entre deux factures.
   */
  const resetInvoiceAnalysis = ({ preserveExchangeRate = false } = {}) => {
    form.resetFields();
    form.setFieldsValue({ expense_date: dayjs(), status: 'paid' });
    setPendingFile(null);
    setRawAnalysisData(null);
    setInvoiceCurrency(null);
    if (!preserveExchangeRate) setExchangeRate(1);
    setDuplicateWarning(null);
  };

  /**
   * Ouvrir la modale pour créer une nouvelle dépense
   * Réinitialise aussi les états liés à l'analyse IA
   */
  const handleCreate = () => {
    setEditingExpense(null);
    resetInvoiceAnalysis();
    setInvoiceQueue([]);
    setCurrentInvoiceIndex(-1);
    setIsModalVisible(true);
  };

  /**
   * Ouvrir la modale pour modifier une dépense existante
   * @param {Object} expense - La dépense à modifier
   */
  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setInvoiceQueue([]);
    setCurrentInvoiceIndex(-1);
    setPendingFile(null);
    setDuplicateWarning(null);
    setRawAnalysisData(null);
    setInvoiceCurrency(null);
    setExchangeRate(1);
    form.setFieldsValue({
      ...expense,
      expense_date: dayjs(expense.expense_date),
      tags: expense.tags || []
    });
    setIsModalVisible(true);
  };

  /**
   * Supprimer une dépense
   * @param {string} id - L'ID de la dépense à supprimer
   */
  const handleDelete = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      message.success('Dépense supprimée avec succès');
      fetchExpenses();
      fetchStats();
      fetchCategoryData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      message.error('Erreur lors de la suppression de la dépense');
    }
  };

  /**
   * Ouvre la modale de consultation du justificatif associé à une dépense.
   * Cette action utilise uniquement l'URL déjà enregistrée : elle ne modifie ni la dépense,
   * ni le fichier stocké dans Supabase.
   * @param {Object} expense - Dépense contenant l'URL du fichier à afficher.
   */
  const handlePreviewReceipt = (expense) => {
    setPreviewingReceipt(expense);
  };

  /**
   * Ferme la modale de consultation et libère la référence de la dépense prévisualisée.
   * Aucun fichier n'est supprimé ou modifié pendant cette fermeture.
   */
  const handleCloseReceiptPreview = () => {
    setPreviewingReceipt(null);
  };

  /**
   * Identifie le type du justificatif à partir de son extension, sans tenir compte
   * des éventuels paramètres présents dans l'URL publique de stockage.
   * @param {string} receiptUrl - URL publique ou externe du justificatif.
   * @returns {'image'|'pdf'|'other'} Type de prévisualisation utilisable.
   */
  const getReceiptPreviewType = (receiptUrl) => {
    const filename = receiptUrl.split(/[?#]/)[0].toLowerCase();

    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(filename)) return 'image';
    if (/\.pdf$/.test(filename)) return 'pdf';

    return 'other';
  };

  /**
   * Ferme la modale sans enregistrer la facture actuellement affichée ni les factures restantes.
   * Les fichiers restent uniquement en mémoire navigateur : aucun document non validé n'est donc envoyé au stockage.
   */
  const handleCancelModal = () => {
    setIsModalVisible(false);
    resetInvoiceAnalysis();
    setInvoiceQueue([]);
    setCurrentInvoiceIndex(-1);
  };

  /**
   * Ignore une facture identifiée comme doublon sans créer une seconde dépense, puis reprend la file.
   * Cette action est disponible seulement durant un import multiple afin qu'un document déjà enregistré
   * ne bloque pas l'analyse et l'ajout des factures suivantes.
   */
  const handleSkipDuplicate = async () => {
    const nextInvoiceIndex = currentInvoiceIndex + 1;
    const nextFile = invoiceQueue[nextInvoiceIndex];

    if (!nextFile) {
      message.info('La dernière facture était déjà enregistrée.');
      handleCancelModal();
      return;
    }

    resetInvoiceAnalysis({ preserveExchangeRate: true });
    setCurrentInvoiceIndex(nextInvoiceIndex);
    await analyzeReceiptFile(nextFile);
  };

  /**
   * Soumettre le formulaire (création ou modification)
   * @param {Object} values - Les valeurs du formulaire
   */
  const handleSubmit = async (values) => {
    // Bloquer si doublon détecté : l'utilisateur doit d'abord effacer le numéro de facture
    if (duplicateWarning) {
      message.error('Cette facture est déjà enregistrée. Modifiez ou supprimez le numéro de facture pour continuer.');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        ...values,
        expense_date: values.expense_date.toISOString(),
        amount: parseFloat(values.amount)
      };

      let requestData;
      let config = {};

      if (pendingFile) {
        // Envoyer en multipart/form-data pour inclure le fichier (upload au moment de la sauvegarde)
        const formData = new FormData();
        Object.entries(payload).forEach(([key, val]) => {
          if (val !== undefined && val !== null) {
            formData.append(key, Array.isArray(val) ? JSON.stringify(val) : val);
          }
        });
        formData.append('file', pendingFile);
        requestData = formData;
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      } else {
        requestData = payload;
      }

      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, requestData, config);
        message.success('Dépense modifiée avec succès');
      } else {
        await api.post('/expenses', requestData, config);
        message.success('Dépense créée avec succès');
      }

      const nextInvoiceIndex = currentInvoiceIndex + 1;
      const nextFile = !editingExpense ? invoiceQueue[nextInvoiceIndex] : null;

      if (nextFile) {
        // La dépense courante est déjà enregistrée : préparer et analyser la suivante sans fermer la modale.
        resetInvoiceAnalysis({ preserveExchangeRate: true });
        setCurrentInvoiceIndex(nextInvoiceIndex);
        await analyzeReceiptFile(nextFile);
      } else {
        setIsModalVisible(false);
        resetInvoiceAnalysis();
        setInvoiceQueue([]);
        setCurrentInvoiceIndex(-1);
      }

      fetchExpenses();
      fetchStats();
      fetchCategoryData();

    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      message.error(
        editingExpense
          ? 'Erreur lors de la modification de la dépense'
          : 'Erreur lors de la création de la dépense'
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Réinitialiser tous les filtres
   */
  const handleResetFilters = () => {
    setFilters({
      category: null,
      status: null,
      dateRange: [dayjs().subtract(30, 'day'), dayjs()],
      search: ''
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  /**
   * Gérer le changement de pagination
   * @param {number} page - Numéro de la page
   * @param {number} pageSize - Taille de la page
   */
  const handleTableChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize
    }));
  };

  /**
   * Obtenir la couleur d'une catégorie
   * @param {string} category - La catégorie
   * @returns {string} - La couleur associée
   */
  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : 'default';
  };

  /**
   * Obtenir le label d'une catégorie
   * @param {string} category - La catégorie
   * @returns {string} - Le label associé
   */
  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  /**
   * Obtenir la couleur d'un statut
   * @param {string} status - Le statut
   * @returns {string} - La couleur associée
   */
  const getStatusColor = (status) => {
    const stat = statuses.find(s => s.value === status);
    return stat ? stat.color : 'default';
  };

  /**
   * Obtenir le label d'un statut
   * @param {string} status - Le statut
   * @returns {string} - Le label associé
   */
  const getStatusLabel = (status) => {
    const stat = statuses.find(s => s.value === status);
    return stat ? stat.label : status;
  };

  // Colonnes du tableau des dépenses
  const columns = [
    {
      title: 'Date',
      dataIndex: 'expense_date',
      key: 'expense_date',
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
      width: 120,
      sorter: true
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag color={getCategoryColor(category)}>
          {getCategoryLabel(category)}
        </Tag>
      ),
      width: 180
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#ff4d4f' }}>
          {new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: getDeviseActive(),
            minimumFractionDigits: 0
          }).format(amount)}
        </Text>
      ),
      width: 150,
      align: 'right'
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusLabel(status)}
        </Tag>
      ),
      width: 120
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.receipt_url && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handlePreviewReceipt(record)}
              size="small"
              aria-label={`Voir le justificatif de la dépense : ${record.description}`}
              title="Voir le justificatif"
            />
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Modifier
          </Button>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette dépense ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Supprimer
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: 230,
      fixed: 'right'
    }
  ];

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      {/* En-tête */}
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Title level={isMobile ? 4 : 2}>
            <WalletOutlined /> Gestion des Dépenses
          </Title>
          <Text type="secondary">
            Suivez toutes vos dépenses pour calculer votre rentabilité
          </Text>
        </Col>
        <Col xs={24} sm={{ flex: 'none' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size="large"
            block={isMobile}
          >
            Nouvelle Dépense
          </Button>
        </Col>
      </Row>

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total des Dépenses"
              value={stats?.totalExpenses || 0}
              prefix={getDeviseActive()}
              precision={0}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Nombre de Dépenses"
              value={stats?.expensesCount || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Dépense Moyenne"
              value={stats?.averageExpense || 0}
              prefix={getDeviseActive()}
              precision={0}
            />
          </Card>
        </Col>
      </Row>

      {/* Graphique de répartition */}
      {categoryData.length > 0 && (
        <Card
          title="Répartition des Dépenses par Catégorie"
          style={{ marginBottom: 24 }}
        >
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={isMobile ? 70 : 100}
                fill="#8884d8"
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: getDeviseActive(),
                  minimumFractionDigits: 0
                }).format(value)}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Filtres */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Rechercher..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Catégorie"
              value={filters.category}
              onChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              {categories.map(cat => (
                <Option key={cat.value} value={cat.value}>
                  <Tag color={cat.color}>{cat.label}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Statut"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              {statuses.map(stat => (
                <Option key={stat.value} value={stat.value}>
                  {stat.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
              locale={locale}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={{ flex: 'none' }}>
            <Button
              icon={<FilterOutlined />}
              onClick={handleResetFilters}
              block={isMobile}
            >
              Réinitialiser
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau des dépenses */}
      <Card>
        {isMobile ? (
          /* Mobile : une carte par dépense (description en haut, puis date/catégorie/montant/statut, actions en bas) */
          <ColumnsCardList
            columns={columns}
            dataSource={expenses}
            loading={loading}
            headerKeys={['description']}
            actionKeys={['actions']}
            emptyText="Aucune dépense trouvée"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: handleTableChange
            }}
          />
        ) : (
        <Table
          columns={columns}
          dataSource={expenses}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} dépenses`,
            onChange: handleTableChange
          }}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <Empty
                description="Aucune dépense trouvée"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
        />
        )}
      </Card>

      {/* Modale de lecture seule pour consulter un justificatif déjà associé à une dépense. */}
      <Modal
        title="Justificatif de dépense"
        open={Boolean(previewingReceipt)}
        onCancel={handleCloseReceiptPreview}
        footer={null}
        width={800}
        destroyOnClose
      >
        {previewingReceipt && (() => {
          const previewType = getReceiptPreviewType(previewingReceipt.receipt_url);
          const receiptLabel = `Justificatif de la dépense : ${previewingReceipt.description}`;

          if (previewType === 'image') {
            return (
              <img
                src={previewingReceipt.receipt_url}
                alt={receiptLabel}
                style={{ display: 'block', maxWidth: '100%', maxHeight: '70vh', margin: '0 auto' }}
              />
            );
          }

          if (previewType === 'pdf') {
            return (
              <iframe
                src={previewingReceipt.receipt_url}
                title={receiptLabel}
                style={{ width: '100%', height: '70vh', border: 0 }}
              />
            );
          }

          return (
            <Alert
              type="info"
              showIcon
              message="Ce type de fichier ne peut pas être prévisualisé ici."
              description={
                <a href={previewingReceipt.receipt_url} target="_blank" rel="noreferrer">
                  Ouvrir le fichier dans un nouvel onglet
                </a>
              }
            />
          );
        })()}
      </Modal>

      {/* Modale de création/modification */}
      <Modal
        title={editingExpense ? 'Modifier la Dépense' : 'Nouvelle Dépense'}
        open={isModalVisible}
        onCancel={handleCancelModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="category"
            label="Catégorie"
            rules={[{ required: true, message: 'Veuillez sélectionner une catégorie' }]}
          >
            <Select placeholder="Sélectionnez une catégorie">
              {categories.map(cat => (
                <Option key={cat.value} value={cat.value}>
                  <Tag color={cat.color}>{cat.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label={`Montant (${getDeviseActive()})`}
            rules={[
              { required: true, message: 'Veuillez saisir le montant' },
              { type: 'number', min: 0, message: 'Le montant doit être positif' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Ex: 50000"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={value => value.replace(/\s?/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="expense_date"
            label="Date de la Dépense"
            rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              locale={locale}
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Veuillez saisir une description' }]}
          >
            <TextArea
              rows={3}
              placeholder="Décrivez la dépense..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="Statut"
            rules={[{ required: true, message: 'Veuillez sélectionner un statut' }]}
          >
            <Select placeholder="Sélectionnez un statut">
              {statuses.map(stat => (
                <Option key={stat.value} value={stat.value}>
                  {stat.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Analyse IA de la Facture">
            {/* Options d'analyse : langue, devise, taux de conversion */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {/* Sélecteur de langue pour la réponse de l'IA */}
              <Select
                value={analyzeLanguage}
                onChange={setAnalyzeLanguage}
                style={{ flex: 1 }}
                placeholder="Langue"
              >
                <Option value="fr">🇫🇷 Français</Option>
                <Option value="en">🇬🇧 English</Option>
                <Option value="ar">🇲🇦 عربي</Option>
                <Option value="es">🇪🇸 Español</Option>
                <Option value="pt">🇵🇹 Português</Option>
              </Select>

              {/* Sélecteur de devise de l'utilisateur */}
              <Select
                value={analyzeCurrency}
                onChange={(v) => { setAnalyzeCurrency(v); setExchangeRate(1); setInvoiceCurrency(null); }}
                style={{ flex: 1 }}
                placeholder="Ma devise"
                showSearch
              >
                <Option value="XOF">XOF — Franc CFA</Option>
                <Option value="XAF">XAF — Franc CFA (CEMAC)</Option>
                <Option value="EUR">€ EUR — Euro</Option>
                <Option value="USD">$ USD — Dollar US</Option>
                <Option value="MAD">MAD — Dirham Marocain</Option>
                <Option value="GNF">GNF — Franc Guinéen</Option>
                <Option value="DZD">DZD — Dinar Algérien</Option>
                <Option value="TND">TND — Dinar Tunisien</Option>
              </Select>
            </div>

            {/* Avertissement et saisie du taux si devise différente détectée */}
            {invoiceCurrency && invoiceCurrency !== analyzeCurrency && (
              <div style={{ marginBottom: 8 }}>
                <Alert
                  type="warning"
                  showIcon
                  message={
                    <span>
                      Devise de la facture : <strong>{invoiceCurrency}</strong> — entrez le taux vers <strong>{analyzeCurrency}</strong>.
                      Le montant sera mis à jour automatiquement.
                    </span>
                  }
                  style={{ marginBottom: 6 }}
                />
                {/* Champ taux de conversion : modifie le montant localement sans rappel IA */}
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.0001}
                  step={0.01}
                  value={exchangeRate}
                  onChange={(v) => setExchangeRate(v || 1)}
                  addonBefore={`1 ${invoiceCurrency} =`}
                  addonAfter={analyzeCurrency}
                  placeholder="Ex: 655.96"
                />
              </div>
            )}

            {/* Zone de dépôt du fichier */}
            <Dragger
              name="file"
              showUploadList={false}
              accept="image/*,application/pdf"
              multiple={!editingExpense}
              beforeUpload={handleReceiptQueue}
              disabled={analyzingFile}
            >
              <p className="ant-upload-drag-icon">
                {analyzingFile ? <Spin indicator={<RobotOutlined spin style={{ fontSize: 48 }} />} /> : <InboxOutlined />}
              </p>
              <p className="ant-upload-text">Cliquez ou glissez {editingExpense ? 'la facture' : 'vos factures'} ici</p>
              <p className="ant-upload-hint">
                {editingExpense
                  ? "L'IA analysera automatiquement le document (Images & PDF)."
                  : "Vous pouvez sélectionner plusieurs documents : l'IA les analysera un par un."}{' '}
                Langue : <strong>{analyzeLanguage.toUpperCase()}</strong> — Devise : <strong>{analyzeCurrency}</strong>
              </p>
            </Dragger>

            {/* Indique clairement quelle facture est prête à être contrôlée puis ajoutée. */}
            {!editingExpense && currentInvoiceIndex >= 0 && invoiceQueue.length > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message={`Facture ${currentInvoiceIndex + 1} sur ${invoiceQueue.length} : ${invoiceQueue[currentInvoiceIndex]?.name}`}
                description={
                  currentInvoiceIndex < invoiceQueue.length - 1
                    ? 'Vérifiez les données, puis ajoutez cette dépense pour analyser la facture suivante.'
                    : 'Dernière facture de la sélection. Vérifiez les données avant de l’ajouter.'
                }
              />
            )}
          </Form.Item>

          {/* Alerte doublon : affichée si la même facture a déjà été saisie */}
          {duplicateWarning && (
            <div style={{ marginBottom: 16 }}>
              <Alert
                type="error"
                showIcon
                message="Facture déjà enregistrée"
                description={
                  <span>
                    Cette facture correspond à une dépense existante :
                    <br />
                    <strong>{duplicateWarning.description}</strong> — {duplicateWarning.amount?.toLocaleString()} {analyzeCurrency}
                    <br />
                    <small>Enregistrée le {dayjs(duplicateWarning.expense_date).format('DD/MM/YYYY')}</small>
                  </span>
                }
              />
              {!editingExpense && currentInvoiceIndex >= 0 && (
                <Button
                  type="link"
                  danger
                  onClick={handleSkipDuplicate}
                  loading={analyzingFile}
                  style={{ paddingLeft: 0, marginTop: 6 }}
                >
                  {currentInvoiceIndex < invoiceQueue.length - 1
                    ? 'Ignorer ce doublon et analyser la suivante'
                    : 'Ignorer ce doublon'}
                </Button>
              )}
            </div>
          )}

          {/* Numéro de facture extrait automatiquement : en lecture seule mais modifiable */}
          <Form.Item
            name="invoice_number"
            label="N° de Facture / Reçu (extrait automatiquement)"
          >
            <Input
              placeholder="Ex: FAC-2024-00123"
              prefix={<FileTextOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="receipt_url"
            label="Lien du Reçu (Généré automatiquement ou lien externe)"
          >
            <Input
              placeholder="https://..."
              prefix={<UploadOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes Additionnelles (optionnel)"
          >
            <TextArea
              rows={2}
              placeholder="Notes supplémentaires..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancelModal}>
                Annuler
              </Button>
              {/* Désactivé si doublon détecté : l'utilisateur doit effacer le numéro de facture */}
              <Button type="primary" htmlType="submit" loading={submitting} disabled={!!duplicateWarning || analyzingFile}>
                {editingExpense
                  ? 'Modifier'
                  : currentInvoiceIndex >= 0 && currentInvoiceIndex < invoiceQueue.length - 1
                    ? 'Ajouter et analyser la suivante'
                    : 'Créer'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Expenses;
